import React from "react";
import ReactMarkdown from "react-markdown";

/**
 * Shared Markdown renderer for changelog bodies (modal + full page). Posts
 * are authored as Markdown in the central `posts` table and rendered the
 * same way on the public marketing blog. react-markdown does not render raw
 * HTML by default, so this is safe without an extra sanitizer.
 */

// react-markdown passes the rendered children through {...p}, so jsx-a11y sees
// <h2 {...p} /> and <a {...p} /> as empty and reports heading-has-content and
// anchor-has-content. The content is real — it comes from the markdown — and
// there is nothing to fix in these lines.
//
// Disabled here rather than "fixed": adding a dummy child to satisfy a linter
// that cannot see a spread would put a wrong element in every heading.
/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mdComponents: any = {
  h1: ({ node, ...p }: any) => <h2 className="text-xl font-semibold mt-4 mb-2" {...p} />,
  h2: ({ node, ...p }: any) => <h2 className="text-lg font-semibold mt-4 mb-2" {...p} />,
  h3: ({ node, ...p }: any) => <h3 className="text-base font-semibold mt-3 mb-1.5" {...p} />,
  p: ({ node, ...p }: any) => <p className="mb-3 leading-relaxed" {...p} />,
  ul: ({ node, ...p }: any) => <ul className="list-disc pl-5 mb-3 space-y-1" {...p} />,
  ol: ({ node, ...p }: any) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...p} />,
  li: ({ node, ...p }: any) => <li {...p} />,
  a: ({ node, ...p }: any) => (
    <a className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer" {...p} />
  ),
  strong: ({ node, ...p }: any) => <strong className="font-semibold" {...p} />,
  em: ({ node, ...p }: any) => <em className="italic" {...p} />,
  code: ({ node, ...p }: any) => (
    <code className="px-1 py-0.5 rounded bg-black/10 dark:bg-white/10 text-[0.9em]" {...p} />
  ),
  blockquote: ({ node, ...p }: any) => (
    <blockquote className="border-l-2 border-gray-300 dark:border-gray-600 pl-3 italic my-3" {...p} />
  ),
};

const MarkdownBody: React.FC<{ children: string; className?: string }> = ({
  children,
  className,
}) => (
  <div className={className}>
    <ReactMarkdown components={mdComponents}>{children || ""}</ReactMarkdown>
  </div>
);

export default MarkdownBody;
